// src/pages/property-types/PropertyTypeCreatePage.tsx
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {ArrowLeft, Save} from "lucide-react";
import {FormField, PageHeader, PropertyTypeIconUpload} from "../../components/common";
import {Button, Card} from "../../components/ui";
import {usePropertyTypes} from "../../hooks/usePropertyTypes";
import {usePropertyTypeIcon} from "../../hooks/usePropertyTypeIcon";
import {useToast} from "../../hooks/common/useToast";
import {ROUTES} from "../../utils/constants";
import type {CreatePropertyTypeDto} from "../../types/property-type";

const PropertyTypeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const {success, error} = useToast();
  const {handleCreate, isCreating} = usePropertyTypes();

  const [formData, setFormData] = useState<CreatePropertyTypeDto>({
    nameEng: "",
    nameMm: "",
    icon: "",
  });

  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const {uploadIcon} = usePropertyTypeIcon();

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nameEng.trim()) {
      newErrors.nameEng = "English name is required";
    } else if (formData.nameEng.length > 100) {
      newErrors.nameEng = "English name must be less than 100 characters";
    }

    if (!formData.nameMm.trim()) {
      newErrors.nameMm = "Myanmar name is required";
    } else if (formData.nameMm.length > 100) {
      newErrors.nameMm = "Myanmar name must be less than 100 characters";
    }

    // Icon validation is now handled by the upload component

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submission started');
    console.log('iconFile:', iconFile);
    console.log('iconPreview:', iconPreview);

    if (!validateForm()) {
      error("Validation Error", "Please fix the errors and try again");
      return;
    }

    try {
      // First create the property type without icon
      const createData: CreatePropertyTypeDto = {
        nameEng: formData.nameEng.trim(),
        nameMm: formData.nameMm.trim(),
      };

      console.log('Creating property type with data:', createData);
      const result = await handleCreate(createData);
      console.log('Property type creation result:', result);
      
      if (result && result.data) {
        const newPropertyTypeId = result.data.id;
        console.log('New property type ID:', newPropertyTypeId);
        
        // If there's an icon file, upload it after creating the property type
        if (iconFile) {
          console.log('Uploading icon for property type:', newPropertyTypeId);
          try {
            const iconResult = await uploadIcon(newPropertyTypeId, iconFile);
            console.log('Icon upload result:', iconResult);
          } catch (iconError) {
            console.error('Icon upload error:', iconError);
            // Property type was created but icon upload failed
            error("Icon Upload Failed", "Property type created but icon upload failed. You can add the icon later by editing the property type.");
          }
        } else {
          console.log('No icon file to upload');
        }
        
        navigate(ROUTES.PROPERTY_TYPES);
      }
    } catch (createError) {
      console.error('Property type creation error:', createError);
      // Error already handled by handleCreate
    }
  };

  // Handle field changes
  const handleFieldChange = (
    field: keyof CreatePropertyTypeDto,
    value: string
  ) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ""}));
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Add New Property Type"
        breadcrumbs={[
          {label: "Dashboard", path: "/dashboard"},
          {label: "Property Types", path: ROUTES.PROPERTY_TYPES},
          {label: "Add New"},
        ]}
      >
        <Button
          variant="secondary"
          onClick={() => navigate(ROUTES.PROPERTY_TYPES)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Property Types
        </Button>
      </PageHeader>

      {/* Form */}
      <Card className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                name="nameEng"
                label="Name (English)"
                type="text"
                value={formData.nameEng}
                onChange={(value) => handleFieldChange("nameEng", value)}
                error={errors.nameEng || ""}
                required
                placeholder="Enter property type name in English"
                disabled={isCreating}
              />

              <FormField
                name="nameMm"
                label="Name (Myanmar)"
                type="text"
                value={formData.nameMm}
                onChange={(value) => handleFieldChange("nameMm", value)}
                error={errors.nameMm || ""}
                required
                placeholder="Enter property type name in Myanmar"
                disabled={isCreating}
              />
            </div>
          </div>

          {/* Icon Upload */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Property Type Icon
            </label>
            
            <div className="flex items-start space-x-4">
              {/* Upload Area */}
              <div
                className="relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-200 w-32 h-32 flex items-center justify-center border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                onClick={() => document.getElementById('icon-upload')?.click()}
              >
                {iconPreview ? (
                  <img
                    src={iconPreview}
                    alt="Property type icon preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">Click to upload</p>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex flex-col space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('icon-upload')?.click()}
                  disabled={isCreating}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Icon
                </Button>
                
                {iconFile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIconFile(null);
                      setIconPreview(null);
                    }}
                    disabled={isCreating}
                    className="text-red-600 hover:text-red-700"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* File Input */}
            <input
              id="icon-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                console.log('File selected:', file);
                if (file) {
                  console.log('File details:', {
                    name: file.name,
                    size: file.size,
                    type: file.type
                  });
                  
                  // Validate file
                  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
                  if (!allowedTypes.includes(file.type)) {
                    error("Invalid File Type", "Please select a valid image file (JPEG, PNG, WEBP, GIF)");
                    return;
                  }
                  
                  // Validate file size (max 5MB)
                  const maxSize = 5 * 1024 * 1024;
                  if (file.size > maxSize) {
                    error("File Too Large", "File size must be less than 5MB");
                    return;
                  }
                  
                  console.log('Setting icon file:', file);
                  setIconFile(file);
                  
                  // Create preview
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    console.log('Setting preview');
                    setIconPreview(e.target?.result as string);
                  };
                  reader.readAsDataURL(file);
                } else {
                  console.log('No file selected');
                }
              }}
              className="hidden"
              disabled={isCreating}
            />

            {/* Help Text */}
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>Upload an icon for this property type (JPEG, PNG, WEBP, GIF)</p>
              <p>Maximum file size: 5MB</p>
              <p>Recommended size: 64x64px or larger</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-dark-600">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(ROUTES.PROPERTY_TYPES)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isCreating} disabled={isCreating}>
              <Save className="w-4 h-4 mr-2" />
              Create Property Type
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PropertyTypeCreatePage;
